import { FC, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { CalculatorOutlined, FileProtectOutlined, InfoCircleOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { Form as FormAntd, Table, TableColumnsType, Tooltip, Typography } from 'antd';
import { RadioChangeEvent } from 'antd/lib';

import { ActionButton } from '@hdi-seguros/web-components.action-button';
import { Card } from '@hdi-seguros/web-components.card';
import { Modal } from '@hdi-seguros/web-components.modal';

import {
  ColSize,
  RadioForm,
  RowSize,
} from '../../../../../../../../../../../shared/components';

import { StepThreeFormContext } from '../../../../contexts/StepThreeForm';
import { WizardDataFormContext } from '../../../../../../Wizard/contexts/WizardDataForm';
import { StepTwoFormContext } from '../../../../../StepTwo/contexts/StepTwoForm';

import { _renderColumns } from '../../../../../../../../../../production/InsurancePolicyDetail/utils/renderSections';
import {
  formattedPrice,
} from '../../../../../../../../../../../shared/utils';

import { COLORS } from '../../../../../../../../../../../config/themes/colors';
import {
  CarQuotationCoverage,
  DECISION_TYPE,
} from '../../../../../../../../../../../shared/interfaces';
import {  QUOTATION_BUTTON, QuotationInfo,  } from '../../../../../../../shared';

interface CardCoverageProps {
  tabKey: number;
  coverage: CarQuotationCoverage;
  quotationInfo: QuotationInfo | undefined;
}



const columnSizeCoverageCard = {
  lg: { span: 6 },
  md: { span: 6 },
  sm: { span: 24 },
  xl: { span: 6 },
  xs: { span: 24 },
};

const columnSizeDetailCalculationColumns = {
  lg: { span: 4 },
  md: { span: 4 },
  sm: { span: 24 },
  xl: { span: 4 },
  xs: { span: 24 },
};

export const CardCoverage: FC<CardCoverageProps> = ({ tabKey, coverage, quotationInfo }) => {
  
  const [canShowCalculation, setCanShowCalculation] = useState<boolean>(false);
  const [canShowCoverage, setCanShowCoverage] = useState<boolean>(false);

  const { stepThreeForm, selectCoveragePerVehicle } = useContext(
    WizardDataFormContext
  );

  const { getInsuredSumaPerVehicle, getDetailCoverage, allCoveragesDetailsByCode } = useContext(StepThreeFormContext);

  const { tabs, labelButton } = useContext(StepTwoFormContext);

  const MOCK_TABLE_COLUMNS_HEADER: TableColumnsType<any> = [
    {
      title: 'Límite de Resp. Civil',
      dataIndex: 'civilLiabilityLimit',
      align: 'center',
      key: 'civilLiabilityLimit'
    },
    {
      title: 'Daños al Vehículo Cláusula “CG-DA”',
      align: 'center',
      key: 'clauseDamagedVehicle',
      children: [
        {
          title: 'Daños por Accidente',
          align: 'center',
          key: 'damageByAccident',
          children: [
            {
              title: 'Total',
              dataIndex: 'totalDamageByAccident',
              align: 'center',
              key: 'totalDamageByAccident',
            },
            {
              title: 'Parcial',
              dataIndex: 'partialDamageByAccident',
              align: 'center',
              key: 'partialDamageByAccident',
            }
          ]
        },
        {
          title: 'Daños por Incendio',
          align: 'center',
          dataIndex: 'age',
          key: 'age',
          children: [
            {
              title: 'Total',
              dataIndex: 'totalDamageByFire',
              align: 'center',
              key: 'totalDamageByFire',
            },
            {
              title: 'Parcial',
              dataIndex: 'partialDamageByFire',
              align: 'center',
              key: 'partialDamageByFire',
            }
          ]
        }
      ]
    },
    {
      colSpan: 2,
      title: 'Robo y/o Hurto Cláusula “CG-RH”',
      align: 'center',
      key: 'clauseCoverageOneTheft',
      children: [
        {
          title: 'Total',
          dataIndex: 'totalTheft',
          align: 'center',
          key: 'totalTheft',
        },
        {
          title: 'Parcial',
          dataIndex: 'partialTheft',
          align: 'center',
          key: 'partialTheft',
        }
      ]
    },
    {
      colSpan: 2,
      title: 'Franquicia',
      dataIndex: 'deductibleAmountOne',
      align: 'center',
      key: 'deductibleAmountOne',
      children: [
        {
          title: `$${formattedPrice(coverage.importeFranquicia)}`,
          align: 'center',
          className: 'cellValueFranchise',
          key: 'franchiseAmountValue',
          children: [
            {
              title: 'Si',
              align: 'center',
              dataIndex: 'yesFranchise',
              key: 'yesFranchise',
            },
            {
              title: 'No',
              align: 'center',
              dataIndex: 'noFranchise',
              key: 'noFranchise',
            }
          ]
        }
      ]
    },
    {
      title: 'Cláusula de Ajuste Automático',
      dataIndex: 'automaticAdjustmentClause',
      align: 'center'
    },
  ];

  const coverageSelected: string = FormAntd.useWatch(
    `codCoverage-${tabKey}`,
    stepThreeForm
  );

  const coverageDetail = useMemo(() => {
    return allCoveragesDetailsByCode?.find((el) => el.coverageCode === coverage.codigo)?.coverageDetail
  }, [allCoveragesDetailsByCode, coverage])

  const handleViewDetailCoverage = useCallback( async () => {
    try {
      await getDetailCoverage(coverage.codigo)
      setCanShowCoverage(true);
    } catch (error) {}
  }, [getDetailCoverage, coverage, setCanShowCoverage])
  
  const coverageInfoColumns = useMemo(() => {
    const columns = [
      {
        label: 'Descripción',
        value: coverage?.descripcion,
        columnSize: columnSizeCoverageCard,
        isVisible: true,
      },
      {
        label: 'Rastreo satelital',
        value: coverage?.requiereRastreador === DECISION_TYPE.YES ? 'Si' : 'No',
        columnSize: columnSizeCoverageCard,
        isVisible: true,
      },
      {
        label: '¿Requiere inspección?',
        value: coverage?.requiereInspeccion === DECISION_TYPE.YES ? 'Si' : 'No',
        columnSize: columnSizeCoverageCard,
        isVisible: true,
      },
      {
        label: 'Valor asegurado',
        value: `$${formattedPrice(
          String(getInsuredSumaPerVehicle(String(tabKey)))
        )}`,
        columnSize: columnSizeCoverageCard,
        isVisible: true,
      },
      {
        label: 'Franquicia',
        value: <b>{`$${formattedPrice(coverage?.importeFranquicia)}`}</b>,
        columnSize: columnSizeCoverageCard,
        isVisible: true,
      },
      {
        label: 'Prima',
        value:
          labelButton === QUOTATION_BUTTON.RECOTIZAR ? (
            QUOTATION_BUTTON.RECOTIZAR
          ) : (
            <b>{`$${formattedPrice(coverage?.prima)}`}</b>
          ),
        columnSize: columnSizeCoverageCard,
        isVisible: true,
      },
      {
        label: 'Premio',
        value:
          labelButton === QUOTATION_BUTTON.RECOTIZAR ? (
            QUOTATION_BUTTON.RECOTIZAR
          ) : (
            <b>{`$${formattedPrice(coverage?.premio)}`}</b>
          ),
        columnSize: columnSizeCoverageCard,
        isVisible: tabs.length === 1,
      },
      {
        label: 'Comisión',
        value:
          labelButton === QUOTATION_BUTTON.RECOTIZAR ? (
            QUOTATION_BUTTON.RECOTIZAR
          ) : (
            <b>{`$${formattedPrice(
              coverage?.importesCobertura?.importeComision
            )}`}</b>
          ),
        columnSize: columnSizeCoverageCard,
        isVisible: true,
      },
      {
        label: 'Detalle cobertura',
        value: (
          <ActionButton
            className='btn-accent-outline-barbuss'
            type='secondary'
            label='Ver cobertura'
            icon={<FileProtectOutlined />}
            onClick={handleViewDetailCoverage}
          />
        ),
        columnSize: columnSizeCoverageCard,
        isVisible: true,
      },
      {
        label: 'Detalle de cálculo',
        value: (
          <ActionButton
            className='btn-accent-outline-barbuss'
            type='secondary'
            label='Ver cálculo'
            icon={<CalculatorOutlined />}
            onClick={() => setCanShowCalculation(true)}
          />
        ),
        columnSize: columnSizeCoverageCard,
        isVisible: tabs.length === 1,
      },
    ];

    return columns.filter((el) => el.isVisible);
  }, [
    coverage,
    setCanShowCalculation,
    tabs,
    labelButton,
    getInsuredSumaPerVehicle,
    tabKey,
    handleViewDetailCoverage
  ]);

  const detailCalculationColumns = useMemo(
    () => [
      {
        label: 'Cobertura',
        value: coverage.codigo,
        columnSize: columnSizeDetailCalculationColumns,
      },
      {
        label: 'Prima',
        value: `$${formattedPrice(coverage?.prima)}`, // todo: verificar que sea este campo..
        columnSize: columnSizeDetailCalculationColumns,
      },
      {
        label: 'Extra prima fija',
        value: `$${formattedPrice(
          coverage?.importesCobertura?.importeExtraPrimaFija
        )}`,
        columnSize: columnSizeDetailCalculationColumns,
      },
      {
        label: 'Recargo financiero',
        value: `$${formattedPrice(
          coverage?.importesCobertura?.importeRecargoFinanciero
        )}`,
        columnSize: columnSizeDetailCalculationColumns,
      },
      {
        label: 'Impuestos',
        value: `$${formattedPrice(
          coverage?.importesCobertura?.importeImpuestosInternos
        )}`, // todo: ver si es este.
        columnSize: columnSizeDetailCalculationColumns,
      },
      {
        label: 'Premio',
        value: `$${formattedPrice(coverage?.premio)}`, // todo: ver si es este..
        columnSize: columnSizeDetailCalculationColumns,
      },
    ],
    [coverage]
  );

  const onSelectCoverage = useCallback(
    async (event: RadioChangeEvent) => {
      if (event.target.checked) {
        await selectCoveragePerVehicle(coverage.codigo, String(tabKey));
        stepThreeForm?.resetFields(['variation']);
      }
    },
    [tabKey, coverage, stepThreeForm, selectCoveragePerVehicle]
  );

    

  const tooltipSubTextCoverage = (coverage: CarQuotationCoverage) => {
    if (!coverage.subCategoriaDeTextos || coverage.subCategoriaDeTextos.length === 0) {
      return null;
    }
    const text = coverage.subCategoriaDeTextos
      .sort((a, b) => Number(a.lineaDeTexto) - Number(b.lineaDeTexto))
      .map(item => item.renglonDeTexto)
      .join('\n');
    return (
      <Tooltip title={text} >
        <span className='subcategory-tooltip-icon' aria-label='Ver texto de subcategoría'>
          <QuestionCircleOutlined />
        </span>
      </Tooltip>
    );
  };

  const dataSourceCoverageDetail = useMemo(() => {    
    const dataCoverageDetail = {
      key: coverage.codigo,
      civilLiabilityLimit: `$${formattedPrice(coverage.limiteRCAcontecimiento)}`,
      automaticAdjustmentClause: `${formattedPrice(coverage.porcentajeClausulaDeAjuste)} %`,
      totalDamageByAccident: coverageDetail?.accidenteTotal,
      partialDamageByAccident: coverageDetail?.accidenteParcial,
      totalDamageByFire: coverageDetail?.incendioTotal,
      partialDamageByFire: coverageDetail?.incendioParcial,
      totalTheft: coverageDetail?.roboTotal,
      partialTheft: coverageDetail?.roboParcial,
      yesFranchise: Number(coverage.importeFranquicia) !== 0 ? 'X': '',
      noFranchise:  Number(coverage.importeFranquicia) === 0 ? 'X': ''
    }

    return [dataCoverageDetail];

  }, [coverage, coverageDetail]) 

  return (
    <>
      <div className='w-100 card-coverage'>
        <Card
          style={{
            backgroundColor:
              coverage.codigo === coverageSelected
                ? COLORS.principal[10]
                : COLORS.neutrals[0],
          }}
        >
          <RowSize rowProps={{ align: 'middle', justify: 'center' }}>
            <ColSize size={10}>
              <ColSize size={100}>
                <RowSize rowProps={{ justify: 'center' }}>
                  <Typography.Text>Cód. cobertura</Typography.Text>
                </RowSize>
                <RowSize rowProps={{ justify: 'center' }}>
                  <Typography.Text strong style={{ fontSize: 30 }}>
                    {coverage.codigo}
                    
                  </Typography.Text>
                   {tooltipSubTextCoverage(coverage)}
                </RowSize>
                <RowSize rowProps={{ justify: 'center' }}>
                  <RadioForm
                    formProps={{ name: `codCoverage-${tabKey}` }}
                    options={[{ value: coverage?.codigo }]}
                    radioProps={{ onChange: onSelectCoverage }}
                  />
                </RowSize>
              </ColSize>
            </ColSize>
            <ColSize size={80}>
              <RowSize>{_renderColumns(coverageInfoColumns)}</RowSize>
            </ColSize>
          </RowSize>
        </Card>
      </div>

      <Modal
        centered
        className='card-coverage-quotation-vehicle'
        footer={
          <ActionButton
            className='btn-accent-outline-barbuss'
            label='Cerrar'
            type='secondary'
            onClick={() => setCanShowCoverage(false)}
            width={115}
          />
        }
        onCancel={() => setCanShowCoverage(false)}
        open={canShowCoverage}
        title='Detalle de cobertura'
        width={1000}
      >
        <Table
          bordered
          className='mb-1'
          columns={MOCK_TABLE_COLUMNS_HEADER}
          dataSource={dataSourceCoverageDetail}
          pagination={false}
        />
      </Modal>

      <Modal
        centered
        footer={
          <ActionButton
            className='btn-accent-outline-barbuss'
            label='Cerrar'
            type='secondary'
            onClick={() => setCanShowCalculation(false)}
            width={115}
          />
        }
        className='mt-1'
        onCancel={() => setCanShowCalculation(false)}
        open={canShowCalculation}
        title='Detalle de cálculo'
        width={1000}
      >
        <Card title='' style={{ marginBottom: 10 }}>
          <RowSize>{_renderColumns(detailCalculationColumns)}</RowSize>
        </Card>
      </Modal>
    </>
  );
};
